<template>
  <div class="sal">
    <form class="sal__card" @submit.prevent="onSubmit">
      <div class="sal__brand">
        <span class="mdi mdi-shield-lock-outline"></span>
        <span class="sal__brand-text">Parkos Admin</span>
      </div>
      <h1 class="sal__title">Confirma tu identidad para acceder al panel de administración</h1>
      <p v-if="route.query.expired" class="sal__notice">Tu sesión expiró. Inicia sesión de nuevo para continuar.</p>

      <template v-if="!challenge">
        <label class="sal__label" for="sal-email">Correo</label>
        <input id="sal-email" v-model.trim="email" type="email" class="sal__input" autocomplete="username" required />

        <label class="sal__label" for="sal-pass">Contraseña</label>
        <input id="sal-pass" v-model="password" type="password" class="sal__input" autocomplete="current-password" required />
      </template>

      <template v-else>
        <p class="sal__hint">Te enviamos un código de 6 dígitos a <strong>{{ challenge.email }}</strong>. Vence en 10 minutos.</p>
        <label class="sal__label" for="sal-code">Código de verificación</label>
        <input id="sal-code" v-model.trim="code" type="text" inputmode="numeric" maxlength="6" pattern="[0-9]{6}" class="sal__input sal__input--code" autocomplete="one-time-code" required />
      </template>

      <p v-if="error" class="sal__error">{{ error }}</p>

      <button type="submit" class="sal__submit" :disabled="busy">{{ busy ? 'Verificando…' : (challenge ? 'Confirmar código' : 'Acceder') }}</button>
      <div v-if="challenge" class="sal__links">
        <button type="button" class="sal__link" :disabled="busy" @click="onResend">Enviar otro código</button>
        <button type="button" class="sal__link" :disabled="busy" @click="onBack">Volver</button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useSuperadminStore } from '@/services/superadminStore'

const route = useRoute()
const router = useRouter()
const { signIn, verifyCode, resendCode } = useSuperadminStore()

const email = ref('')
const password = ref('')
const busy = ref(false)
const error = ref('')
const challenge = ref(null) // { challengeId, email } mientras se espera el código del segundo paso
const code = ref('')

const MESSAGES = {
  INVALID_CREDENTIALS: 'Correo o contraseña incorrectos.',
  TOO_MANY_ATTEMPTS: 'Demasiados intentos fallidos. Espera 15 minutos.',
  MISSING_FIELDS: 'Ingresa tu correo y contraseña.',
  INVALID_CODE: 'Código incorrecto.',
  CODE_EXPIRED: 'El código venció o ya se usó. Pide otro.',
  TOO_MANY_CODES: 'Pediste demasiados códigos. Espera unos minutos.',
  WAIT_BEFORE_RESEND: 'Espera unos segundos antes de pedir otro código.',
}

function finish() {
  const redirect = typeof route.query.redirect === 'string' && route.query.redirect.startsWith('/superadmin')
    ? route.query.redirect : '/superadmin/parques'
  router.push(redirect)
}

async function onSubmit() {
  busy.value = true
  error.value = ''
  try {
    if (challenge.value) {
      await verifyCode(challenge.value.challengeId, code.value)
      finish()
      return
    }
    const result = await signIn(email.value, password.value)
    if (result && result.twoFactorRequired) {
      challenge.value = { challengeId: result.challengeId, email: result.email }
      code.value = ''
      return
    }
    finish()
  } catch (err) {
    error.value = MESSAGES[err.message] || 'No se pudo iniciar sesión.'
  } finally {
    busy.value = false
  }
}

async function onResend() {
  busy.value = true
  error.value = ''
  try {
    const result = await resendCode(email.value, password.value)
    challenge.value = { challengeId: result.challengeId, email: result.email }
    code.value = ''
  } catch (err) {
    error.value = MESSAGES[err.message] || 'No se pudo enviar otro código.'
  } finally {
    busy.value = false
  }
}

function onBack() {
  challenge.value = null
  code.value = ''
  password.value = ''
  error.value = ''
}
</script>

<style scoped>
.sal { min-height: 100vh; display: flex; align-items: center; justify-content: center; background: #1e3a5f; padding: 1rem; }
.sal__card {
  width: 100%; max-width: 380px; background: #fff; border-radius: 12px; padding: 2rem 1.75rem;
  display: flex; flex-direction: column; border-top: 4px solid #e84545; box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
}
.sal__brand { display: flex; align-items: center; gap: 8px; color: #1e3a5f; margin-bottom: 1rem; }
.sal__brand .mdi { font-size: 22px; color: #e84545; }
.sal__brand-text { font: 700 16px var(--font-display); }
.sal__title { margin: 0 0 1.25rem; font: 500 14px Inter, sans-serif; color: #475569; line-height: 1.4; }
.sal__label { font: 400 12px Inter, sans-serif; color: #64748b; margin-bottom: 4px; }
.sal__input { font: 400 14px Inter, sans-serif; color: #1e3a5f; border: 1px solid #d5dbe6; border-radius: 8px; padding: 10px 12px; margin-bottom: 14px; }
.sal__input:focus { outline: none; border-color: #3b82f6; }
.sal__hint { margin: 0 0 14px; font: 400 13px Inter, sans-serif; color: #475569; line-height: 1.5; }
.sal__input--code { font: 600 22px var(--font-mono); letter-spacing: 0.4em; text-align: center; }
.sal__links { display: flex; justify-content: space-between; margin-top: 12px; }
.sal__link { background: none; border: none; padding: 0; font: 400 12px Inter, sans-serif; color: #3b82f6; cursor: pointer; }
.sal__link:disabled { opacity: 0.6; cursor: default; }
.sal__notice { margin: -4px 0 12px; padding: 8px 10px; border-radius: 6px; background: #fef3c7; font: 400 12px Inter, sans-serif; color: #92400e; }
.sal__error { margin: -4px 0 12px; font: 400 12px Inter, sans-serif; color: #e84545; }
.sal__submit { background: #1e3a5f; color: #fff; border: none; border-radius: 8px; padding: 11px; font: 500 14px Inter, sans-serif; cursor: pointer; }
.sal__submit:hover:not(:disabled) { background: #163049; }
.sal__submit:disabled { opacity: 0.7; cursor: default; }
</style>